/**
 * What the open modals hold of the page (TOADO-984): the page behind them
 * made inert, and where focus goes back to when each closes.
 *
 * Inert rather than a Tab trap. A trap had to know every way focus can leave
 * a dialog, and missed some: an iframe, a popover drawn outside the dialog, a
 * control that uses Tab itself. `inert` takes the page behind out of the tab
 * order, the pointer and the accessibility tree at once, and leaves alone
 * whatever is not part of it.
 *
 * What counts as the page behind: the app's root, the layers of the shared
 * modals opened before this one, and any other dialog layer on the body (an
 * element holding an `aria-modal` dialog, such as the ticket's). Nothing else
 * on the body is touched, deliberately: a popover or tooltip a dialog opens
 * is drawn on the body and has to stay usable, and so do layers that are not
 * the app's, such as the Toado extension's pin panel, the bug widget, a tour's
 * card and the toasts' region. A layer that is not a dialog must not claim
 * `aria-modal`, or it is taken for one.
 */

/** The element the app renders into, as apps/web/index.html names it. */
const appRoot = (): HTMLElement | null => document.getElementById('root');

/** A native dialog shown as a modal one: `:modal` where the engine knows it, an open one where it does not (review round 3). */
const MODAL_DIALOG = (() => {
  try {
    if (typeof document !== 'undefined') document.querySelector('dialog:modal');
    return 'dialog:modal';
  } catch {
    return 'dialog[open]';
  }
})();

/** Marks a shared modal's own layer on the body, so the stack, not the markup, says how it stands to another. */
export const MODAL_LAYER_ATTRIBUTE = 'data-vd-modal-layer';

/** Marks a popover's root, so a dialog opened from inside one can find what opened the popover. */
export const POPOVER_ATTRIBUTE = 'data-vd-popover';

/**
 * Elements held inert, each with the count of modals holding it, so a modal
 * closing out of order lets go of only its own hold. One already inert when
 * the first modal came is someone else's, and never touched.
 */
const holds = new Map<HTMLElement, number>();

function holdInert(element: HTMLElement): () => void {
  const count = holds.get(element);
  if (count === undefined) {
    if (element.hasAttribute('inert')) return () => {};
    element.setAttribute('inert', '');
    holds.set(element, 1);
  } else {
    holds.set(element, count + 1);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const left = (holds.get(element) ?? 1) - 1;
    if (left > 0) {
      holds.set(element, left);
      return;
    }
    holds.delete(element);
    element.removeAttribute('inert');
  };
}

/** An open modal, oldest first: the newest answers Escape, and where focus goes back is handed down as they close. */
export interface OpenModal {
  /** Opened later than every modal with a lower one; ordering two opened in one commit too. */
  seq: number;
  /** Where focus goes when this modal closes, if nothing else has taken it. */
  opener: HTMLElement | null;
  /** What opened the popover the opener was in, for when a menu item that opened this has gone (review round 1). */
  origin: HTMLElement | null;
  dialog: HTMLElement | null;
  /** This modal's own element on the body. */
  layer: HTMLElement | null;
}

export const openModals: OpenModal[] = [];
let opened = 0;

/** Puts a modal on the stack as it opens. */
export function openModal(entry: Omit<OpenModal, 'seq'>): OpenModal {
  const self = { ...entry, seq: (opened += 1) };
  openModals.push(self);
  return self;
}

/** Whether a shared modal is open, for a page's own shortcuts to keep quiet meanwhile (review round 1). */
export function isModalOpen(): boolean {
  // For the whole page: every shared modal in it counts, wherever it was opened.
  return openModals.length > 0;
}

/**
 * Makes the page behind a modal inert: the app's root, the layers of shared
 * modals already on the stack (two opened in one commit hold only the one
 * before: review round 1), and dialog layers on the body that are not a shared
 * modal's. Returns the release.
 */
export function holdPageBehind(entry: OpenModal): () => void {
  const behind = new Set<HTMLElement>();
  const root = appRoot();
  if (root && entry.layer && !root.contains(entry.layer)) behind.add(root);
  for (const older of openModals) {
    if (older.seq < entry.seq && older.layer?.isConnected) behind.add(older.layer);
  }
  for (const child of Array.from(document.body.children)) {
    if (child === entry.layer || !(child instanceof HTMLElement) || child.hasAttribute(MODAL_LAYER_ATTRIBUTE)) continue;
    if (child.matches('[aria-modal="true"]') || child.querySelector('[aria-modal="true"]')) behind.add(child);
  }
  const releases = [...behind].map(holdInert);
  return () => releases.forEach((release) => release());
}

/**
 * Whether something opened over this modal now holds the page: a shared
 * modal opened after it, or a dialog of another kind (the ticket's, a native
 * dialog) that this modal was not opened from (review round 1).
 */
function anotherDialogOnTop(entry: OpenModal, opener: HTMLElement): boolean {
  if (openModals.some((modal) => modal.seq > entry.seq)) return true;
  // A native dialog counts only when shown as a modal one (review round 2).
  for (const dialog of Array.from(document.querySelectorAll(`[aria-modal="true"], ${MODAL_DIALOG}`))) {
    if (dialog.closest(`[${MODAL_LAYER_ATTRIBUTE}]`)) continue;
    // Held inert behind the shared modals still open, it is under them, not on
    // top: the ticket a Move dialog was opened from (review round 5). Only an
    // older shared modal still holds anything inert here, since this one has
    // let go of its own, and it held only what was there before it opened.
    if (dialog.closest('[inert]')) continue;
    if (!dialog.contains(opener)) return true;
  }
  return false;
}

/**
 * Takes a modal off the stack as it closes. A newer modal still open is
 * handed this one's opener where its own was inside this dialog, so focus
 * still finds its way back when that one closes. Returns the giving back of
 * focus, for the caller to run once the close is known to be one (a
 * StrictMode replay of the effect is not), or nothing when there is none.
 */
export function closeModal(entry: OpenModal): (() => void) | null {
  const index = openModals.indexOf(entry);
  if (index < 0) return null;
  openModals.splice(index, 1);
  const newer = openModals.slice(index);
  if (newer.length > 0) {
    for (const above of newer) {
      if (!above.opener || !above.opener.isConnected || entry.dialog?.contains(above.opener)) {
        above.opener = entry.opener;
        above.origin = entry.origin;
      }
    }
    return null;
  }
  return () => {
    /**
     * A modal opened in the same click as this one closed (one dialog swapped
     * for another) went on the stack after this came off it, so it was not
     * handed this opener above; its own is inside this dialog, now gone. Handed
     * it here instead, so focus finds its way back when that one closes
     * (review round 2).
     */
    const replacements = openModals.filter((modal) => modal.seq > entry.seq);
    if (replacements.length > 0) {
      for (const above of replacements) {
        if (!above.opener || !above.opener.isConnected || entry.dialog?.contains(above.opener)) {
          above.opener = entry.opener;
          above.origin = entry.origin;
        }
      }
      return;
    }
    // Only while nothing else has taken focus: it is still in the dialog, or
    // the dialog has gone and left it on the body.
    const active = document.activeElement;
    const focusIsFree = !active || active === document.body || !active.isConnected || !!entry.dialog?.contains(active);
    const opener = [entry.opener, entry.origin].find((element) => element?.isConnected && !element.closest('[inert]'));
    if (!opener) {
      // Out of reach under an older dialog still open: focus goes to that dialog,
      // not nowhere (review round 7).
      const under = openModals.at(-1)?.dialog;
      if (focusIsFree && under?.isConnected) under.focus({ preventScroll: true });
      return;
    }
    if (anotherDialogOnTop(entry, opener)) return;
    if (focusIsFree) opener.focus({ preventScroll: true });
  };
}

/** The element focus is on now, if it can be given focus back later. */
export function currentFocus(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

/** What a press can act on: the control a pointer or Enter and Space went to. */
const ACTIVATABLE = 'button, a[href], input, select, textarea, summary, [role="button"], [role="menuitem"], [role="tab"], [tabindex]';

/** How recent a press must be to have opened what opens now: the same click, give or take its handlers. */
const ACTIVATION_WINDOW_MS = 1_000;

/**
 * The control the person last pressed, and when (review round 2). Safari does
 * not focus a button that is clicked, so focus alone cannot say what opened a
 * dialog or a menu: it is on the page body, or on whatever had it before.
 */
let lastActivated: { element: HTMLElement; at: number } | null = null;

function noteActivation(event: Event): void {
  // Any other key is the latest thing done, and was not a press of the control
  // noted: a shortcut opening a dialog after it is not opened by that control
  // (review round 3).
  if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
    lastActivated = null;
    return;
  }
  const element = event.target instanceof Element ? event.target.closest<HTMLElement>(ACTIVATABLE) : null;
  // A press on nothing pressable is still the latest press: it says nothing opened what opens next.
  lastActivated = element ? { element, at: performance.now() } : null;
}

/**
 * Focus moving to another control means the press is over: it opens nothing
 * later (review round 3). Focus lost rather than moved, a control blurring
 * itself before it opens a dialog, fires no focusin, so it keeps the press.
 */
function noteFocus(event: FocusEvent): void {
  const pressed = lastActivated?.element;
  if (pressed && event.target instanceof Node && !pressed.contains(event.target)) lastActivated = null;
}

/**
 * The press ends with the gesture (review round 4): whatever the click or key
 * opens, it opens in its own handlers, and React renders that before a timer
 * can run. A dialog opened later, by a timer or a message from the server, was
 * not opened by it.
 */
function endPress(): void {
  const pressed = lastActivated;
  if (!pressed) return;
  setTimeout(() => {
    if (lastActivated === pressed) lastActivated = null;
  }, 0);
}

if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', noteActivation, true);
  document.addEventListener('keydown', noteActivation, true);
  document.addEventListener('focusin', noteFocus, true);
  document.addEventListener('click', endPress, true);
  document.addEventListener('keyup', endPress, true);
}

/**
 * What opened a dialog or a menu opening now: the control pressed for it just
 * now, or else the element focus is on. A dialog opened after an await has
 * neither for certain (in Safari a click leaves focus on nothing, and the
 * press has ended by then), so its caller passes `returnFocusRef`: a guess
 * from the last click named controls that opened nothing (review round 6).
 */
export function invoker(): HTMLElement | null {
  const pressed = lastActivated;
  if (pressed && pressed.element.isConnected && performance.now() - pressed.at < ACTIVATION_WINDOW_MS) return pressed.element;
  return currentFocus();
}

/** What opened each popover open now: the element focus was on as it opened. */
const popoverOrigins = new WeakMap<Element, HTMLElement>();

export function rememberPopoverOrigin(popover: HTMLElement, origin: HTMLElement | null): void {
  if (origin) popoverOrigins.set(popover, origin);
  else popoverOrigins.delete(popover);
}

/** What opened the popover an element is in, if it is in one. */
export function popoverOriginOf(element: HTMLElement | null): HTMLElement | null {
  const popover = element?.closest(`[${POPOVER_ATTRIBUTE}]`);
  return popover ? popoverOrigins.get(popover) ?? null : null;
}
